import { useEffect, useState } from 'react';
// Import the primitive directly (not via the ./ui barrel) so the entry chunk
// doesn't reach through to Modal -> motion/react. CookieBanner ships statically
// from main.tsx, so any tree-shaking miss here lands ~120 KB of motion in the
// landing-page entry bundle.
import { GlassPanel } from './ui/GlassPanel';
import { initAnalytics } from '../lib/analytics';
import { ensurePostHog, getPostHog } from '../lib/posthog-lazy';

/**
 * GDPR + ePrivacy cookie consent banner. Renders at root if the user has not
 * yet recorded a consent decision. Initialises PostHog ONLY after explicit
 * consent for the Analytics category — required for legal-defensibility in EU
 * traffic. See docs/audit/gdpr-ccpa-2026-05-13.md.
 *
 * Consent record lives in localStorage under `cookieConsent` as JSON:
 *   { analytics: boolean, marketing: boolean, ts: number }
 *
 * The Necessary category covers auth + billing cookies — always on, not
 * separately togglable in the UI.
 */

const STORAGE_KEY = 'cookieConsent';

export interface ConsentRecord {
  analytics: boolean;
  marketing: boolean;
  ts: number;
}

/** Read the current consent record, or null if the user has not yet decided. */
export function readConsent(): ConsentRecord | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as ConsentRecord;
    if (typeof parsed.analytics !== 'boolean') return null;
    return parsed;
  } catch {
    return null;
  }
}

function writeConsent(record: ConsentRecord) {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(record));
  } catch {
    // ignore — Safari private mode etc. The banner will just re-show next visit.
  }
}

/**
 * Idempotent PostHog bootstrap. Safe to call from anywhere — bails out unless
 * analytics consent has been granted. Used by main.tsx (initial mount) and by
 * the banner itself (post-consent activation in the same tab).
 */
export function maybeInitAnalyticsWithConsent() {
  const consent = readConsent();
  if (consent?.analytics) {
    void initAnalytics();
  }
}

export function CookieBanner() {
  const [visible, setVisible] = useState(false);
  const [showCustomize, setShowCustomize] = useState(false);
  const [analyticsOn, setAnalyticsOn] = useState(true);
  const [marketingOn, setMarketingOn] = useState(true);

  useEffect(() => {
    setVisible(readConsent() === null);
  }, []);

  const persist = (analytics: boolean, marketing: boolean) => {
    writeConsent({ analytics, marketing, ts: Date.now() });
    if (analytics) {
      // Light-up PostHog in the current session (otherwise user has to refresh).
      void initAnalytics();
      // If already initialised on a previous visit, opt back in.
      void ensurePostHog().then((ph) => {
        if (typeof ph.opt_in_capturing === 'function') ph.opt_in_capturing();
      }).catch(() => { /* posthog-js not loaded — nothing to do */ });
    } else {
      // Withdraw: opt out of capturing in the current session — only if it's
      // already in memory; otherwise there's nothing to withdraw.
      const ph = getPostHog();
      if (ph && typeof ph.opt_out_capturing === 'function') {
        ph.opt_out_capturing();
      }
    }
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      role="dialog"
      aria-modal="false"
      aria-labelledby="cookie-banner-title"
      className="fixed bottom-4 left-4 right-4 md:left-6 md:right-auto md:max-w-md z-50"
    >
      <GlassPanel padding="md" className="shadow-xl">
        <h2
          id="cookie-banner-title"
          className="text-base text-indigo-950 mb-2"
          style={{ fontFamily: 'var(--font-display)' }}
        >
          We use cookies
        </h2>
        <p className="text-sm text-slate-600 mb-3">
          Necessary cookies keep you signed in and handle billing. We'd also like to use
          analytics cookies (PostHog) to improve the product. Your choice — no tracking
          until you accept.
        </p>

        {showCustomize && (
          <div className="space-y-2 mb-3 text-sm">
            <label className="flex items-center justify-between gap-3 py-1">
              <span className="text-slate-700">
                Necessary
                <span className="block text-xs text-slate-500">Auth, billing — always on.</span>
              </span>
              <input type="checkbox" checked disabled className="w-4 h-4" />
            </label>
            <label className="flex items-center justify-between gap-3 py-1">
              <span className="text-slate-700">
                Analytics
                <span className="block text-xs text-slate-500">Product analytics and session replay.</span>
              </span>
              <input
                type="checkbox"
                checked={analyticsOn}
                onChange={(e) => setAnalyticsOn(e.target.checked)}
                className="w-4 h-4"
              />
            </label>
            <label className="flex items-center justify-between gap-3 py-1">
              <span className="text-slate-700">
                Marketing
                <span className="block text-xs text-slate-500">Reserved for future ad pixels — none used today.</span>
              </span>
              <input
                type="checkbox"
                checked={marketingOn}
                onChange={(e) => setMarketingOn(e.target.checked)}
                className="w-4 h-4"
              />
            </label>
          </div>
        )}

        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => persist(true, true)}
            className="rounded-md bg-indigo-600 text-white px-3 py-2 text-sm font-semibold hover:bg-indigo-700"
          >
            Accept all
          </button>
          <button
            type="button"
            onClick={() => persist(false, false)}
            className="rounded-md border border-slate-300 bg-white text-slate-700 px-3 py-2 text-sm font-semibold hover:bg-slate-50"
          >
            Necessary only
          </button>
          {showCustomize ? (
            <button
              type="button"
              onClick={() => persist(analyticsOn, marketingOn)}
              className="rounded-md border border-indigo-200 bg-indigo-50 text-indigo-700 px-3 py-2 text-sm font-semibold hover:bg-indigo-100"
            >
              Save choices
            </button>
          ) : (
            <button
              type="button"
              onClick={() => setShowCustomize(true)}
              className="rounded-md border border-slate-200 bg-white text-slate-600 px-3 py-2 text-sm hover:bg-slate-50"
            >
              Customize
            </button>
          )}
        </div>
        <p className="mt-2 text-xs text-slate-500">
          See our <a href="/privacy" className="underline">Privacy Policy</a>.
        </p>
      </GlassPanel>
    </div>
  );
}
